/*
  <tab-switcher> - reusable accessible tabs.

  Markup contract (tabs and panels can sit anywhere inside the element):

  <tab-switcher>
    <div role="tablist">
      <button role="tab" aria-controls="panel-1" aria-selected="true" class="is-active">One</button>
      <button role="tab" aria-controls="panel-2" aria-selected="false">Two</button>
    </div>

    <div role="tabpanel" id="panel-1">...</div>
    <div role="tabpanel" id="panel-2" hidden>...</div>
  </tab-switcher>

  The element marks the active tab with the class "is-active" and aria-selected,
  and shows the matching panel by toggling its "hidden" attribute and the class
  "is-on". It also supports arrow keys / Home / End and the theme editor's
  block selection (a panel carrying the block attributes opens its tab).
*/
if (!customElements.get('tab-switcher')) {
  class TabSwitcher extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      if (!this.tabs.length) return;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activate(tab));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });

      // Theme editor: selecting a block opens its tab
      this.onBlockSelect = (event) => {
        const panel = this.panels.find((p) => p === event.target || p.contains(event.target));
        if (!panel) return;
        const tab = this.tabs.find((t) => t.getAttribute('aria-controls') === panel.id);
        if (tab) this.activate(tab);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      const first = this.tabs.find((t) => t.classList.contains('is-active')) || this.tabs[0];
      this.activate(first, false);
    }

    disconnectedCallback() {
      if (this.onBlockSelect) {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    onKeydown(event, index) {
      let next;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % this.tabs.length;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + this.tabs.length) % this.tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.tabs.length - 1;
      else return;

      event.preventDefault();
      this.tabs[next].focus();
      this.activate(this.tabs[next]);
    }

    activate(activeTab, animate = true) {
      const target = activeTab.getAttribute('aria-controls');

      this.tabs.forEach((tab) => {
        const selected = tab === activeTab;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.setAttribute('tabindex', selected ? '0' : '-1');
      });

      this.panels.forEach((panel) => {
        const on = panel.id === target;
        panel.hidden = !on;
        panel.classList.toggle('is-on', on && animate);
      });
    }
  }

  customElements.define('tab-switcher', TabSwitcher);
}
